import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Home, Building, MapPin, Factory, Shield, Plane } from 'lucide-react'

const Applications = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const applications = [
    {
      icon: Home,
      category: "Residential",
      title: "Home & Garden Lighting",
      description: "Perfect for pathway lighting, garden illumination, and emergency lighting in homes.",
      useCases: [
        "Garden pathway lights",
        "Emergency backup lighting",
        "Outdoor decorative lighting",
        "Stairway illumination"
      ],
      benefits: ["No electricity bills", "Easy installation", "Weather resistant", "Safe for families"]
    },
    {
      icon: Building,
      category: "Commercial",
      title: "Office & Retail Spaces",
      description: "Ideal for commercial buildings, shopping centers, and office complexes.",
      useCases: [
        "Exit sign illumination",
        "Parking garage lighting",
        "Retail display lighting",
        "Office corridor lighting"
      ],
      benefits: ["Reduced energy costs", "Low maintenance", "Enhanced safety", "Professional appearance"]
    },
    {
      icon: MapPin,
      category: "Public Infrastructure",
      title: "Street & Highway Lighting",
      description: "Large-scale deployment for municipal and highway lighting systems.",
      useCases: [
        "Street lamp systems",
        "Highway markers",
        "Pedestrian crossings",
        "Public park lighting"
      ],
      benefits: ["Municipal savings", "Reduced grid load", "Improved safety", "Environmental friendly"]
    },
    {
      icon: Factory,
      category: "Industrial",
      title: "Manufacturing & Warehouses",
      description: "Industrial applications for factories, warehouses, and manufacturing facilities.",
      useCases: [
        "Warehouse aisle lighting",
        "Safety zone marking",
        "Emergency evacuation routes",
        "Equipment illumination"
      ],
      benefits: ["24/7 operation", "Explosion safe", "No heat generation", "Minimal maintenance"]
    },
    {
      icon: Shield,
      category: "Safety & Security",
      title: "Emergency & Safety Systems",
      description: "Critical safety applications where reliable lighting is essential.",
      useCases: [
        "Emergency exit signs",
        "Safety perimeter marking",
        "First aid station lighting",
        "Security checkpoint lighting"
      ],
      benefits: ["Always operational", "No power dependency", "Instant activation", "Regulatory compliant"]
    },
    {
      icon: Plane,
      category: "Transportation",
      title: "Airports & Transportation",
      description: "Aviation and transportation infrastructure lighting solutions.",
      useCases: [
        "Airport runway markers",
        "Terminal way-finding",
        "Transit station lighting",
        "Vehicle guidance systems"
      ],
      benefits: ["Flight safety", "Reliable operation", "Weather resistant", "Low operational cost"]
    }
  ]

  return (
    <section id="applications" className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Real-World <span className="text-green-500">Applications</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how Pigmentium™ technology is transforming lighting across industries, 
            from residential homes to major infrastructure projects.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {applications.map((app, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                  <app.icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full font-medium">
                    {app.category}
                  </span>
                  <h3 className="text-2xl font-bold text-gray-800 mt-2">{app.title}</h3>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6 leading-relaxed">{app.description}</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Use Cases:</h4>
                  <ul className="space-y-2">
                    {app.useCases.map((useCase, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-3 flex-shrink-0"></div>
                        <span className="text-sm">{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Benefits:</h4>
                  <ul className="space-y-2">
                    {app.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mr-3 flex-shrink-0"></div>
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full mt-6 bg-gradient-to-r from-green-500 to-blue-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Learn More About {app.category} Solutions
              </motion.button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 bg-gradient-to-r from-green-600 to-blue-700 rounded-2xl p-8 text-white"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Custom Application Development</h3>
              <p className="text-xl mb-6 opacity-90">
                Have a unique lighting challenge? Our engineering team can develop custom Pigmentium™ 
                solutions tailored to your specific application requirements.
              </p>
              <ul className="space-y-2 text-lg">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                  Custom form factors and sizes
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                  Specialized mounting systems
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                  Integration with existing infrastructure
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></div>
                  Performance optimization for specific environments
                </li>
              </ul>
            </div>
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6">
                <h4 className="text-2xl font-bold mb-4">Project Consultation</h4>
                <p className="text-lg opacity-90 mb-4">
                  Speak with our application engineers about your specific requirements.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-yellow-400 text-gray-900 font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Schedule Engineering Consultation
                </motion.button>
              </div>
              <p className="text-sm opacity-75">
                Free consultation • Custom quotes available • Rapid prototyping
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Applications 